import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

const NOTE_MAX = 500;

/**
 * Confirmation modal for host cancelling their own tee time.
 * Replaces window.confirm() so phone/website/cancellation links can be clickable.
 *
 * v84: adds an optional note (textarea) that's delivered to players in the
 * cancellation email and push. Empty by default; if blank when sent, the
 * email omits the note section entirely.
 *
 * Props:
 *   tt          — tee time object (course_name, course_phone, course_url, cancellation_url, players)
 *   currentUser — { id }
 *   onClose     — called when user dismisses without cancelling
 *   onConfirmed — called after the cancel API succeeds
 */
export default function CancelTeeTimeModal({ tt, currentUser, onClose, onConfirmed }) {
  const [phone, setPhone] = useState(tt.course_phone || '');
  const [courseUrl, setCourseUrl] = useState(tt.course_url || '');
  // Cancellation URL is dynamic per-booking — never backfilled. Use only what's saved on this tee time.
  const cancellationUrl = tt.cancellation_url || '';
  const [hostNote, setHostNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Backfill phone and courseUrl from another tee time at the same course if missing.
  // We never backfill cancellationUrl because it's dynamic per booking.
  useEffect(() => {
    if (!tt.course_name) return;
    if (phone && courseUrl) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getCourseContact(tt.course_name);
        if (cancelled) return;
        if (!phone     && data.phone)     setPhone(data.phone);
        if (!courseUrl && data.courseUrl) setCourseUrl(data.courseUrl);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [tt.course_name, phone, courseUrl]);

  const otherPlayers = (tt.players || []).filter(p => p.id !== currentUser.id);
  const playerCount  = otherPlayers.length;
  const hasContact   = !!(phone || courseUrl || cancellationUrl);

  const handleConfirm = async () => {
    setBusy(true); setError('');
    try {
      const trimmed = hostNote.trim();
      await api.deleteTeeTime(tt.id, trimmed ? { hostNote: trimmed } : undefined);
      onConfirmed();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  // Strip phone for tel: link (digits only, keep leading +)
  const telHref = phone ? `tel:${phone.replace(/[^\d+]/g, '')}` : null;
  const noteLen = hostNote.length;
  const noteOverLimit = noteLen > NOTE_MAX;

  return (
    <div className="modal-backdrop" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" style={{ maxWidth: 460 }}>
        <h3 style={{ marginBottom: 6 }}>Cancel this tee time?</h3>
        <div style={{ fontSize: 13, color: '#5a5a4a', marginBottom: 12 }}>
          <strong style={{ color: '#0d2b1a' }}>{tt.course_name}</strong>
        </div>

        {playerCount > 0 && (
          <div style={{ background: '#fff8e8', border: '1px solid #f0d8a0', borderRadius: 8, padding: '0.7rem 0.85rem', marginBottom: 12, fontSize: 13, color: '#7a5a0a' }}>
            <strong>{playerCount}</strong> player{playerCount !== 1 ? 's' : ''} will be notified by email and push.
          </div>
        )}

        {/* Optional host note (v84). Always visible — empty by default. */}
        {playerCount > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label
              htmlFor="host-cancel-note"
              style={{ display: 'block', fontSize: 12, fontWeight: 500, color: '#0d2b1a', marginBottom: 6 }}
            >
              Add a note to players <span style={{ fontWeight: 400, color: '#7a7a6a' }}>(optional)</span>
            </label>
            <textarea
              id="host-cancel-note"
              value={hostNote}
              onChange={(e) => setHostNote(e.target.value)}
              placeholder="Let them know what happened, or where you're going instead."
              disabled={busy}
              rows={3}
              maxLength={NOTE_MAX + 50 /* let the counter warn before hard clip */}
              style={{
                width: '100%', boxSizing: 'border-box', resize: 'vertical',
                padding: '8px 10px', border: '1px solid #e8e4d8',
                borderRadius: 6, fontSize: 13, color: '#0d2b1a',
                fontFamily: 'inherit', lineHeight: 1.4,
                background: '#fff',
              }}
            />
            <div style={{
              fontSize: 11, color: noteOverLimit ? '#c33' : '#9a9a8a',
              textAlign: 'right', marginTop: 2,
            }}>
              {noteLen} / {NOTE_MAX}
            </div>
          </div>
        )}

        {hasContact ? (
          <div style={{ background: '#f4f1eb', borderRadius: 8, padding: '0.85rem', marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: '#0d2b1a', marginBottom: 8 }}>
              Don't forget to cancel directly with the course:
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {phone && (
                <a href={telHref} style={linkStyle}>
                  📞 <span>{phone}</span>
                </a>
              )}
              {courseUrl && (
                <a href={courseUrl} target="_blank" rel="noopener noreferrer" style={linkStyle}>
                  🌐 <span style={{ wordBreak: 'break-all' }}>{courseUrl}</span>
                </a>
              )}
              {cancellationUrl ? (
                <a
                  href={cancellationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                    background: '#2e7d4f', color: '#fff', textDecoration: 'none',
                    padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
                    alignSelf: 'flex-start', marginTop: 2,
                  }}
                >
                  ✂️ Cancel directly with course
                </a>
              ) : (
                <div style={{ fontSize: 11, color: '#9a9a8a', fontStyle: 'italic', marginTop: 2 }}>
                  No cancellation link was found in your email confirmation.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={{ background: '#fff0e8', border: '1px solid #f0c8a8', borderRadius: 8, padding: '0.7rem 0.85rem', marginBottom: 14, fontSize: 12, color: '#7a3a1a' }}>
            ⚠️ This only removes the tee time from Greentime. You will still need to cancel directly with the course.
          </div>
        )}

        {error && <p className="form-error">{error}</p>}

        <div className="modal-actions">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={busy}>Keep tee time</button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={handleConfirm}
            disabled={busy || noteOverLimit}
          >
            {busy ? 'Cancelling…' : 'Cancel tee time'}
          </button>
        </div>
      </div>
    </div>
  );
}

const linkStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  fontSize: 13,
  color: '#2e7d4f',
  textDecoration: 'none',
  padding: '4px 0',
};
